import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from "@mui/material";
import Iconify from "../components/iconify/Iconify";
import {
  useDeleteContractMutation,
  useGetContractsQuery,
  useUpdateContractMutation,
} from "../features/contracts/contractsApiSlice";
import { useGetSitesQuery } from "../features/sites/sitesApiSlice";
import ContractFormDialog from "../sections/@dashboard/contracts/ContractFormDialog";
import ContractDetailsDialog from "../sections/@dashboard/contracts/ContractDetailsDialog";
import { formatIBAN } from "../utils/dataUtils";
import { useGetMembersQuery } from "../features/members/membersApiSlice";
import { setDialog } from "../features/dialog/dialogSlice";

const maskIban = (iban) => {
  if (!iban) return "—";
  const clean = String(iban).replace(/\s/g, "");
  if (clean.length <= 6) return clean;
  const start = clean.slice(0, 4);
  const end = clean.slice(-4);
  return `${start} •••• •••• •••• ${end}`.trim();
};

const getContractName = (contract) =>
  `${contract?.title || ""} ${contract?.firstName || ""} ${
    contract?.lastName || ""
  }`.trim() || "—";

const getSortValue = (contract, sortBy) => {
  switch (sortBy) {
    case "name":
      return getContractName(contract).toLowerCase();
    case "owner":
      return String(contract?.owner || "").toLowerCase();
    case "site":
      return String(contract?.standortRef?.siteName || "").toLowerCase();
    case "members":
      return Number(contract?.hasMembers?.length || 0);
    case "createdAt":
      return new Date(contract?.createdAt || 0).getTime();
    default:
      return String(contract?.[sortBy] || "").toLowerCase();
  }
};

export default function ContractsPage() {
  const {
    data: contractsData,
    isLoading,
    isError,
    error,
    refetch: refetchContracts,
  } = useGetContractsQuery();
  const { data: sites = [] } = useGetSitesQuery();

  const [updateContract, { isLoading: isUpdating }] =
    useUpdateContractMutation();
  const [deleteContract, { isLoading: isDeleting }] =
    useDeleteContractMutation();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [formOpen, setFormOpen] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailsContract, setDetailsContract] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromQueryRef = useRef(false);

  const detailsOpen = Boolean(detailsContract);
  const {
    data: membersData = [],
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorData,
  } = useGetMembersQuery(undefined, { skip: !detailsOpen });

  const contracts = useMemo(() => {
    if (Array.isArray(contractsData)) return contractsData;
    return [];
  }, [contractsData]);

  const noContractsError =
    isError &&
    (error?.status === 400 || error?.status === 404) &&
    String(error?.data?.message || "")
      .toLowerCase()
      .includes("no contracts");

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      const siteName = contract?.standortRef?.siteName || "";
      if (siteFilter !== "all" && String(contract?.standortRef?._id || "") !== siteFilter) {
        return false;
      }
      if (!normalizedSearch) return true;
      const searchFields = [
        getContractName(contract),
        contract?.owner,
        contract?.email,
        contract?.phoneNumber,
        contract?.mobile,
        contract?.bank,
        contract?.bic,
        contract?.iban,
        contract?.city,
        contract?.zip,
        siteName,
      ];
      return searchFields
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [contracts, normalizedSearch, siteFilter]);

  const sortedContracts = useMemo(() => {
    const data = [...filteredContracts];
    const orderMultiplier = sortOrder === "asc" ? 1 : -1;
    data.sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);
      if (aValue < bValue) return -1 * orderMultiplier;
      if (aValue > bValue) return 1 * orderMultiplier;
      return 0;
    });
    return data;
  }, [filteredContracts, sortBy, sortOrder]);

  const paginatedContracts = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedContracts.slice(start, start + rowsPerPage);
  }, [sortedContracts, page, rowsPerPage]);

  const totalMembers = useMemo(
    () =>
      contracts.reduce(
        (sum, contract) => sum + (contract?.hasMembers?.length || 0),
        0,
      ),
    [contracts],
  );

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleOpenNewMemberDialog = () => {
    dispatch(setDialog({ title: "Neues Mitglied erstellen", type: "CREATE" }));
  };

  const handleOpenEdit = (contract) => {
    setActiveContract(contract);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setActiveContract(null);
  };

  const handleFormSubmit = async (payload) => {
    setActionError("");
    try {
      if (activeContract?._id) {
        await updateContract({ ...payload, _id: activeContract._id }).unwrap();
        await refetchContracts();
        setActionSuccess("Vertrag wurde erfolgreich aktualisiert.");
      }
      handleCloseForm();
    } catch (err) {
      const message = err?.data?.message || "Aktion fehlgeschlagen.";
      setActionError(message);
    }
  };

  const handleConfirmDelete = (contract) => {
    setDeleteTarget(contract);
    setActionError("");
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setActionError("");
    try {
      await deleteContract({ id: deleteTarget._id }).unwrap();
      setDeleteTarget(null);
    } catch (err) {
      const message = err?.data?.message || "Löschen fehlgeschlagen.";
      setActionError(message);
    }
  };

  const handleOpenDetails = (contract) => {
    setDetailsContract(contract);
  };

  const handleCloseDetails = () => {
    setDetailsContract(null);
  };

  const contractMembers = useMemo(() => {
    if (!detailsOpen || !detailsContract) return [];
    const contractId = String(detailsContract._id || "");
    return membersData.filter((member) => {
      const ref = member?.contractRef;
      const refId = typeof ref === "object" ? ref?._id : ref;
      return String(refId || "") === contractId;
    });
  }, [detailsOpen, detailsContract, membersData]);

  const isEmpty =
    !isLoading &&
    (sortedContracts.length === 0 || (noContractsError && contracts.length === 0));
  const canRenderTable = !isLoading && sortedContracts.length > 0;

  useEffect(() => {
    const contractId = searchParams.get("contractId");
    if (!contractId || openedFromQueryRef.current || !Array.isArray(contracts)) {
      return;
    }

    const matchedContract = contracts.find(
      (contract) => String(contract?._id) === contractId,
    );
    if (!matchedContract) return;

    openedFromQueryRef.current = true;
    setDetailsContract(matchedContract);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("contractId");
    setSearchParams(nextParams, { replace: true });
  }, [contracts, searchParams, setSearchParams]);

  return (
    <>
      <Helmet>
        <title> Dashboard: Vertragsdaten </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">Vertragsdaten</Typography>
              <Typography variant="body2" color="text.secondary">
                Verträge verwalten, Mitglieder zuordnen und Bankdaten pflegen.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleOpenNewMemberDialog}
            >
              Neuer Vertrag
            </Button>
          </Stack>

          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: "linear-gradient(135deg, #fdf8f2 0%, #eef4ff 100%)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Card sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Verträge gesamt
                </Typography>
                <Typography variant="h4">{contracts.length}</Typography>
              </Card>
              <Card sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mitglieder in Verträgen
                </Typography>
                <Typography variant="h4">{totalMembers}</Typography>
              </Card>
              <Card sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Standorte mit Verträgen
                </Typography>
                <Typography variant="h4">
                  {new Set(
                    contracts
                      .map((contract) => contract?.standortRef?._id)
                      .filter(Boolean),
                  ).size}
                </Typography>
              </Card>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Suche nach Name, IBAN, E-Mail oder Standort"
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                label="Standort"
                value={siteFilter}
                onChange={(event) => {
                  setSiteFilter(event.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">Alle Standorte</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site._id} value={site._id}>
                    {site.siteName}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>

          {isLoading && <LinearProgress />}

          {actionError && (
            <Alert severity="error" onClose={() => setActionError("")}
            >
              {actionError}
            </Alert>
          )}

          {isError && !noContractsError && !actionError && (
            <Alert severity="error">
              {error?.data?.message || "Fehler beim Laden der Verträge."}
            </Alert>
          )}

          {isEmpty && (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6">Noch keine Vertragsdaten</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Lege den ersten Vertrag an, um Mitglieder zuzuordnen.
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 3 }}
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenNewMemberDialog}
              >
                Vertrag anlegen
              </Button>
            </Paper>
          )}

          {canRenderTable && (
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === "name"}
                          direction={sortBy === "name" ? sortOrder : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          Vertragsnehmer
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === "owner"}
                          direction={sortBy === "owner" ? sortOrder : "asc"}
                          onClick={() => handleSort("owner")}
                        >
                          Kontoinhaber
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Kontakt</TableCell>
                      <TableCell>IBAN</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === "site"}
                          direction={sortBy === "site" ? sortOrder : "asc"}
                          onClick={() => handleSort("site")}
                        >
                          Standort
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortBy === "members"}
                          direction={sortBy === "members" ? sortOrder : "asc"}
                          onClick={() => handleSort("members")}
                        >
                          Mitglieder
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === "createdAt"}
                          direction={sortBy === "createdAt" ? sortOrder : "asc"}
                          onClick={() => handleSort("createdAt")}
                        >
                          Erstellt
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedContracts.map((contract) => {
                      const membersCount = contract?.hasMembers?.length || 0;
                      const isPublic =
                        contract?.publicRegistrationMeta?.createdViaPublic;
                      return (
                        <TableRow key={contract._id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2">
                                  {getContractName(contract)}
                                </Typography>
                                {isPublic && (
                                  <Chip
                                    size="small"
                                    label="Öffentlich"
                                    color="info"
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                {contract?.birthdate
                                  ? new Date(
                                      contract.birthdate,
                                    ).toLocaleDateString("de-DE")
                                  : "—"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contract?.owner || "—"}
                            </Typography>
                            {contract?.isOwner === false && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Abweichender Zahler
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contract?.email || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[contract?.phoneNumber, contract?.mobile]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {maskIban(formatIBAN(contract?.iban || ""))}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {contract?.bic || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contract?.standortRef?.siteName || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {contract?.standortRef?.city || ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={membersCount}
                              color={membersCount > 0 ? "primary" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {contract?.createdAt
                              ? new Date(contract.createdAt).toLocaleDateString(
                                  "de-DE",
                                )
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDetails(contract)}
                                >
                                  <Iconify icon="eva:eye-fill" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Bearbeiten">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(contract)}
                                >
                                  <Iconify icon="eva:edit-2-fill" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  membersCount > 0
                                    ? "Löschen nicht möglich: Mitglieder zugeordnet"
                                    : "Löschen"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={membersCount > 0}
                                    onClick={() => handleConfirmDelete(contract)}
                                  >
                                    <Iconify icon="eva:trash-2-outline" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={sortedContracts.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Stack>
      </Container>

      <ContractFormDialog
        open={formOpen}
        mode="edit"
        contract={activeContract}
        sites={sites}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        isSaving={isUpdating}
      />

      <ContractDetailsDialog
        open={detailsOpen}
        contract={detailsContract}
        members={contractMembers}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorMessage={membersErrorData?.data?.message}
        onClose={handleCloseDetails}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Vertrag löschen</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Möchtest du den Vertrag von {getContractName(deleteTarget)} wirklich
            löschen? Dieser Schritt kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(actionSuccess)}
        autoHideDuration={3500}
        onClose={() => setActionSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setActionSuccess("")}
          sx={{ width: "100%" }}
        >
          {actionSuccess}
        </Alert>
      </Snackbar>
    </>
  );
}
